'use client';

import type {
  FixPullRequestSessionResponse,
  FixRunSessionResponse,
  FixSessionDetail,
  FixSessionErrorCode,
  FixSessionListResponse,
} from '@/lib/fix-sessions/types';
import { queryOptions } from '@tanstack/react-query';

import { isLiveRunStatus } from '@/lib/fix-sessions/runs';

/**
 * While a pull request session is live the list polls. Its runs wait their turn and are admitted
 * by the turn engine, which writes no FixSession row, so no realtime hint reports the change.
 */
export const FIX_SESSION_LIST_RUNNING_POLL_MS = 5_000;
/**
 * While a run is live the session page refreshes its transcript on its own. The list follows
 * realtime hints instead (`useFixSessionUpdates`).
 */
export const FIX_SESSION_DETAIL_RUNNING_POLL_MS = 3_000;
/**
 * While a pull request's page has no run to open, how often it looks again: a run's session can
 * start from GitHub (an @cubic comment, Fix with cubic) without the auto-fix update the page
 * listens for. A run auto-fix just started gets its session within seconds, so the page looks
 * every `FIX_SESSION_DETAIL_RUNNING_POLL_MS` until it does.
 */
export const FIX_PULL_REQUEST_SESSION_WAITING_POLL_MS = 15_000;

export const fixSessionKeys = {
  all: ['fix-sessions'] as const,
  list: ['fix-sessions', 'list'] as const,
  /** Under `list`, so the realtime hints that refetch the list refetch the archive too. */
  archivedList: ['fix-sessions', 'list', 'archived'] as const,
  details: ['fix-sessions', 'detail'] as const,
  detail: (sandboxId: string) => ['fix-sessions', 'detail', sandboxId] as const,
  /** A run on code sessions' pull request session, read by the run's id. */
  runSession: (runId: string) => ['fix-sessions', 'run-session', runId] as const,
  /** cubic's session on a pull request, read by the pull request. */
  pullRequest: (owner: string, repo: string, pullNumber: number) =>
    ['fix-sessions', 'pull-request', owner, repo, pullNumber] as const,
};

/** A fix session API refusal: its message, HTTP status and code. */
export class FixSessionRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: FixSessionErrorCode | null,
  ) {
    super(message);
    this.name = 'FixSessionRequestError';
  }
}

/** Statuses that mean the viewer can't open the session; timeouts and rate limits are not. */
const REFUSAL_STATUSES: ReadonlySet<number> = new Set([401, 403, 404]);

/** The session is gone or the viewer can't open it, so retrying or polling won't help. */
export function isFixSessionRefusal(error: unknown): error is FixSessionRequestError {
  return error instanceof FixSessionRequestError && REFUSAL_STATUSES.has(error.status);
}

function fixSessionQueryDefaults() {
  return {
    staleTime: 0,
    placeholderData: undefined,
    retry: (failureCount: number, error: Error) => !isFixSessionRefusal(error) && failureCount < 2,
  };
}

async function readJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, cache: 'no-store' });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      code?: FixSessionErrorCode;
    } | null;
    throw new FixSessionRequestError(
      body?.error ?? `Request failed with ${response.status}`,
      response.status,
      body?.code ?? null,
    );
  }
  return (await response.json()) as T;
}

/**
 * Whether a pull request session or an automated session of the list is still going. A legacy
 * run's row needs no poll: its start and finish write its FixSession row, which sends the realtime
 * hint.
 */
function hasLiveSession(list: FixSessionListResponse): boolean {
  return [...list.pullRequestSessions, ...list.automatedSessions].some((session) =>
    isLiveRunStatus(session.status),
  );
}

async function readFixSessionList(
  url: string,
  signal: AbortSignal,
): Promise<FixSessionListResponse> {
  const list = await readJson<Partial<FixSessionListResponse>>(url, signal);
  // A server older than pull request sessions answers runs alone, and one older than automated
  // sessions answers without them.
  return {
    sessions: list.sessions ?? [],
    pullRequestSessions: list.pullRequestSessions ?? [],
    automatedSessions: list.automatedSessions ?? [],
  };
}

export function fixSessionListOptions() {
  return queryOptions({
    queryKey: fixSessionKeys.list,
    queryFn: ({ signal }) => readFixSessionList('/api/fix-sessions', signal),
    // Sessions start elsewhere (GitHub, the PR page, the loop), so never trust a cached list.
    staleTime: 0,
    placeholderData: undefined,
    refetchInterval: (query) =>
      query.state.data && hasLiveSession(query.state.data)
        ? FIX_SESSION_LIST_RUNNING_POLL_MS
        : false,
  });
}

/**
 * The archive: legacy runs archived with their pull request, and pull request sessions that ended.
 * It rarely changes, so it doesn't poll; the realtime hints refetch it with the list.
 */
export function fixSessionArchivedListOptions() {
  return queryOptions({
    queryKey: fixSessionKeys.archivedList,
    queryFn: ({ signal }) => readFixSessionList('/api/fix-sessions?archived=1', signal),
    staleTime: 0,
    placeholderData: undefined,
  });
}

export function fixSessionDetailOptions(sandboxId: string) {
  return queryOptions({
    queryKey: fixSessionKeys.detail(sandboxId),
    queryFn: ({ signal }) =>
      readJson<FixSessionDetail>(`/api/fix-sessions/${encodeURIComponent(sandboxId)}`, signal),
    ...fixSessionQueryDefaults(),
    refetchInterval: (query) =>
      !isFixSessionRefusal(query.state.error) && query.state.data?.status === 'running'
        ? FIX_SESSION_DETAIL_RUNNING_POLL_MS
        : false,
  });
}

/** Whether a run's page still changes: a run or a pull request session that is still going. */
function isLiveRunSession(response: FixRunSessionResponse | undefined): boolean {
  if (!response) {
    return false;
  }
  return response.type === 'run'
    ? response.status === 'running'
    : isLiveRunStatus(response.session.status);
}

/**
 * The pull request session of a run on code sessions (`code-<turnUuid>`), with the current run in
 * full. Refreshes on its own while any run of the session is live, and never once all finished;
 * the page also refreshes it when the session's turns change.
 */
export function fixRunSessionOptions(runId: string) {
  return queryOptions({
    queryKey: fixSessionKeys.runSession(runId),
    queryFn: ({ signal }) =>
      readJson<FixRunSessionResponse>(
        `/api/fix-sessions/${encodeURIComponent(runId)}/session`,
        signal,
      ),
    ...fixSessionQueryDefaults(),
    refetchInterval: (query) =>
      !isFixSessionRefusal(query.state.error) && isLiveRunSession(query.state.data)
        ? FIX_SESSION_DETAIL_RUNNING_POLL_MS
        : false,
  });
}

/**
 * The run a pull request's page opens (`FixPullRequestSessionResponse`), read by the pull request.
 * Until a run reached a session it looks again on its own: every few seconds while `runStarting`
 * (auto-fix says its run started), else every `FIX_PULL_REQUEST_SESSION_WAITING_POLL_MS`.
 */
export function fixPullRequestSessionOptions(
  pr: { owner: string; repo: string; pullNumber: number },
  { runStarting }: { runStarting: boolean },
) {
  return queryOptions({
    queryKey: fixSessionKeys.pullRequest(pr.owner, pr.repo, pr.pullNumber),
    queryFn: ({ signal }) =>
      readJson<FixPullRequestSessionResponse>(
        `/api/fix-sessions/pr/${encodeURIComponent(pr.owner)}/${encodeURIComponent(pr.repo)}/${encodeURIComponent(String(pr.pullNumber))}`,
        signal,
      ),
    ...fixSessionQueryDefaults(),
    refetchInterval: (query) => {
      if (isFixSessionRefusal(query.state.error) || query.state.data?.type !== 'none') {
        return false;
      }
      return runStarting
        ? FIX_SESSION_DETAIL_RUNNING_POLL_MS
        : FIX_PULL_REQUEST_SESSION_WAITING_POLL_MS;
    },
  });
}
